import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiPlusCircle, FiRefreshCw } from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';

import { Alert } from '../../../../components/_components_0.2/Alert';
import {
  NonConformityIndicators,
  qualityControlService,
} from '../../../../services/qualityControlService';
import { FormLetterhead } from '../FormLetterhead';
import { IndicatorCards } from '../IndicatorCards';
import { PageShell } from '../PageShell';
import { useDemoMode } from '../useDemoMode';
import {
  buildListStatusSearch,
  filterFromIndicator,
  IndicatorKey,
} from '../utils';

const QualityControlDashboard: React.FC = () => {
  const demo = useDemoMode();
  const [indicators, setIndicators] = useState<NonConformityIndicators | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const confirmReset = useDisclosure();
  const toast = useToast();
  const history = useHistory();

  // Cada card abre a lista já filtrada (ex.: "Vencidas" -> ?overdue=1).
  const handleSelectCard = useCallback(
    (key: IndicatorKey) => {
      history.push(
        `/quality-control/non-conformities${buildListStatusSearch(
          filterFromIndicator(key),
          demo,
        )}`,
      );
    },
    [demo, history],
  );

  const loadIndicators = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await qualityControlService.getIndicators(demo);
      setIndicators(data);
    } catch {
      setError(
        'Não foi possível carregar os indicadores do Controle de Qualidade.',
      );
    } finally {
      setLoading(false);
    }
  }, [demo]);

  useEffect(() => {
    loadIndicators();
  }, [loadIndicators]);

  const handleResetDemoData = useCallback(() => {
    qualityControlService.resetDemoStore();
    confirmReset.onClose();
    toast({
      title: 'Dados de demonstração resetados',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    loadIndicators();
  }, [confirmReset, loadIndicators, toast]);

  return (
    <PageShell>
      <FormLetterhead demo={demo} />

      <Flex justify="flex-end" mb="6" flexWrap="wrap" gridGap="3">
        <Button
          as={Link}
          to={`/quality-control/new${demo ? '?demo=1' : ''}`}
          leftIcon={<FiPlusCircle />}
          colorScheme="blue"
        >
          Registrar NC
        </Button>
        <Button
          as={Link}
          to={`/quality-control/non-conformities${demo ? '?demo=1' : ''}`}
          variant="outline"
        >
          Ver Não Conformidades
        </Button>
        {demo && (
          <Button
            leftIcon={<FiRefreshCw />}
            variant="ghost"
            colorScheme="purple"
            onClick={confirmReset.onOpen}
          >
            Resetar dados de demonstração
          </Button>
        )}
      </Flex>

      <Alert
        isOpen={confirmReset.isOpen}
        onClose={confirmReset.onClose}
        handleFunction={handleResetDemoData}
        title="Resetar dados de demonstração"
        textBody="Isso apaga todas as NCs criadas no modo demonstração (guardadas só no seu navegador) e volta pros dados de exemplo originais. Deseja continuar?"
        ButonSave="Resetar"
        ButonClose="Cancelar"
      />

      {loading && (
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      )}

      {!loading && error && <Text color="red.400">{error}</Text>}

      {!loading && !error && indicators && (
        <IndicatorCards indicators={indicators} onSelect={handleSelectCard} />
      )}

      {!demo && !loading && error && (
        <Button
          as={Link}
          to="/quality-control?demo=1"
          size="sm"
          colorScheme="purple"
          variant="outline"
          mt="8"
        >
          Ver em modo de demonstração
        </Button>
      )}

      {demo && (
        <Button
          as={Link}
          to="/quality-control?demo=0"
          size="sm"
          colorScheme="purple"
          variant="outline"
          mt="8"
        >
          Sair do modo de demonstração
        </Button>
      )}
    </PageShell>
  );
};

export default QualityControlDashboard;
